export interface ContactLimitInfo {
  limit: number;
  current: number;
  importCount: number | null;
  recommendedTier: string | null;
}

export const CONTACT_LIMIT_EVENT = "connectyid:contact-limit";

/** The backend's contact-limit refusal (422 + code), or null for anything else. */
export const contactLimitFrom = (error: unknown): ContactLimitInfo | null => {
  const res = (error as any)?.response;
  if (res?.status !== 422 || res?.data?.code !== "CONTACT_LIMIT_REACHED") return null;
  return {
    limit: Number(res.data.limit),
    current: Number(res.data.current_count),
    importCount: res.data.import_count ?? null,
    recommendedTier: res.data.recommended_tier ?? null,
  };
};

export const OVER_LIMIT_EVENT = "connectyid:over-limit";

/** The backend's view-only refusal (403 + code), or null for anything else. */
export const overLimitFrom = (error: unknown): { current: number; limit: number } | null => {
  const res = (error as any)?.response;
  if (res?.status !== 403 || res?.data?.code !== "OVER_CONTACT_LIMIT") return null;
  return { current: Number(res.data.current_count), limit: Number(res.data.limit) };
};

/**
 * The pre-emptive 422 CONTACT_LIMIT_REACHED refusal and the already-over-limit 403
 * OVER_CONTACT_LIMIT refusal are the same story for the import wizard and the
 * registration contact-intake screens: either way, the write didn't go through
 * because the account is at or past its plan's contact limit, and both surfaces
 * show the identical "you're at your plan's limit" message (Task 17, ruling
 * T16→T17 — an over-limit account's import dry-run now answers 403, where Task 11
 * only ever handled the 422). Normalises the 403 shape into ContactLimitInfo so
 * call sites don't need a parallel branch.
 */
export const contactLimitOrOverLimitFrom = (error: unknown): ContactLimitInfo | null => {
  const limit = contactLimitFrom(error);
  if (limit) return limit;
  const over = overLimitFrom(error);
  return over ? { limit: over.limit, current: over.current, importCount: null, recommendedTier: null } : null;
};
